import { Authentication } from '@leedpulse-monorepo/classes';
import { CustomSelect, mergeOptionsWithPinnedLabels, selectedTokens } from '@leedpulse-monorepo/components';
import { Spin } from 'antd';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import searchUsersAlgolia from '../../../../domain/use_cases/users/searchUsersAlgolia';

type CustomSelectProps = ComponentProps<typeof CustomSelect>;

export type UserHit = { uid: string; email: string; phone: string; firstname: string; lastname: string };

export type DebouncedUserSelectProps = Omit<CustomSelectProps, 'options' | 'onSearch' | 'remoteSearch' | 'optionRender'> & {
    auth: Authentication | null | undefined;
    debounceTimeout?: number;
    /** Called when the user selects an option, with the full Algolia hit data. */
    onHitSelect?: (hit: UserHit) => void;
    optionRender?: (hit: UserHit) => React.ReactNode | string;
};

/**
 * CustomSelect with debounced Algolia user search (email as option value).
 * Requires CustomSelect `remoteSearch` so options are updated from the server.
 */
export default function DebouncedUserSelect({
    auth,
    debounceTimeout = 300,
    value,
    onChange,
    onHitSelect,
    optionRender,
    ...selectProps
}: DebouncedUserSelectProps) {
    const [options, setOptions] = useState<{ label: React.ReactNode | string; value: string }[]>([]);
    const [fetching, setFetching] = useState(false);
    const fetchRef = useRef(0);
    const [labelByValue, setLabelByValue] = useState<Record<string, React.ReactNode | string>>({});
    // Stores the full hit data keyed by uid for lookup on selection.
    const hitsMapRef = useRef<Record<string, UserHit>>({});

    const selected = useMemo(() => selectedTokens(value), [value]);

    const selectOptions = useMemo(
        () => mergeOptionsWithPinnedLabels(options, selected, labelByValue),
        [options, selected, labelByValue]
    );

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value: string) => {
            if (!auth) {
                return;
            }

            fetchRef.current += 1;
            const fetchId = fetchRef.current;

            const query = value.trim();
            if (!query) {
                setOptions([]);
                setFetching(false);
                return;
            }

            setOptions([]);
            setFetching(true);

            searchUsersAlgolia(query, 0, 20, auth)
                .then((response) => {
                    if (fetchId !== fetchRef.current) {
                        return;
                    }
                    const next = response.hits.map((user: Record<string, unknown>) => {
                        const uid = String(user.uid ?? user.objectID ?? '');
                        const email = typeof user.email === 'string' ? user.email : '';
                        const phone = typeof user.phone === 'string' ? user.phone : '';
                        const firstname = typeof user.firstname === 'string' ? user.firstname : '';
                        const lastname = typeof user.lastname === 'string' ? user.lastname : '';
                        const hit: UserHit = { uid, email, phone, firstname, lastname };
                        hitsMapRef.current[uid] = hit;
                        const label = optionRender ? optionRender(hit) : email || uid;
                        return { label, value: uid };
                    });
                    setOptions(next);
                    setFetching(false);
                })
                .catch(() => {
                    if (fetchId !== fetchRef.current) {
                        return;
                    }
                    setOptions([]);
                    setFetching(false);
                });
        };

        // react/refs signale une closure lisant une ref passee a une fonction
        // pendant le rendu. `debounce` ne l'appelle jamais de facon synchrone :
        // elle ne s'execute qu'apres le delai, hors rendu.
        // eslint-disable-next-line react/refs
        return debounce(loadOptions, debounceTimeout);
    }, [auth, debounceTimeout]);

    useEffect(() => {
        if (selected.length === 0) {
            // Cache de libelles, pas une derivation : la mise a jour juste en dessous lit
            // prev pour garder le libelle d'une option qui n'est plus dans la page de
            // resultats courante. Un calcul en rendu perdrait ces libelles.
            // eslint-disable-next-line react/set-state-in-effect
            setLabelByValue({});
            return;
        }
        setLabelByValue((prev) => {
            const next: Record<string, React.ReactNode | string> = {};
            for (const v of selected) {
                const opt = options.find((o) => o.value === v);
                if (opt) {
                    next[v] = opt.label;
                } else if (prev[v]) {
                    next[v] = prev[v];
                }
            }
            return next;
        });
    }, [selected, options]);

    const handleChange: CustomSelectProps['onChange'] = (val, opt) => {
        onChange?.(val, opt);
        if (onHitSelect && val && typeof val === 'string') {
            const hit = hitsMapRef.current[val];
            if (hit) onHitSelect(hit);
        }
    };

    return (
        <CustomSelect
            {...selectProps}
            value={value}
            remoteSearch
            filterOption={false}
            options={selectOptions}
            onSearch={debounceFetcher}
            onChange={handleChange}
            notFoundContent={fetching ? <Spin size="small" /> : 'Aucun résultat'}
            loading={fetching}
        />
    );
}
